import React from 'react';

import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

/**
 * NavigationItem Component
 *
 * A reusable navigation item component for mobile menu using shadcn/ui Button.
 * Replaces MUI's StyledListItemButton and StyledListItemIcon with Tailwind CSS.
 *
 * @component
 * @example
 * ```tsx
 * <NavigationItem
 *   menuItem={{
 *     id: 'dashboard',
 *     name: 'Dashboard',
 *     icon: <HomeIcon />
 *   }}
 *   onClick={handleNavigation}
 *   isSelected={currentPath === '/dashboard'}
 *   level="first"
 * />
 * ```
 */

export interface NavigationItemProps {
  /** Menu item data */
  menuItem: {
    /** Unique identifier */
    id: string;
    /** Display name */
    name: string;
    /** Optional icon element */
    icon?: React.ReactNode;
  };
  /** Click handler for navigation */
  onClick: () => void;
  /** Whether this item is currently selected/active */
  isSelected: boolean;
  /** Nesting level for indentation (first, second, third) */
  level?: 'first' | 'second' | 'third';
  /** Additional CSS classes */
  className?: string;
}

/**
 * NavigationItem - Mobile menu navigation button with icon and label
 *
 * Features:
 * - Supports selected/active state with primary color background
 * - Hierarchical indentation based on nesting level
 * - Accessible with proper ARIA attributes
 * - Icon support with consistent spacing
 * - Hover effects with darker background
 * - Bold text for selected state
 *
 * Styling Translation from MUI to shadcn/ui:
 * - MUI `selected` → `bg-primary text-primary-foreground`
 * - MUI `selected:hover` → `hover:bg-primary/90`
 * - MUI `pl: level-based` → `pl-{2,4,6}` (dynamic padding left)
 * - MUI `py: 1` → `py-2.5` (8px top/bottom padding)
 * - ListItemIcon `min-width: 40px` → `min-w-[40px]`
 */
export const NavigationItem: React.FC<NavigationItemProps> = ({
  menuItem,
  onClick,
  isSelected,
  level = 'first',
  className,
}) => {
  // Map level to padding-left (pl) value
  // first = 2 * 4px = 8px = pl-2
  // second = 4 * 4px = 16px = pl-4
  // third = 6 * 4px = 24px = pl-6
  const paddingLeft = {
    first: 'pl-2',
    second: 'pl-4',
    third: 'pl-6',
  }[level];

  return (
    <Button
      type="button"
      variant="ghost"
      className={cn(
        // Base layout - horizontal flex with left alignment
        'flex items-center justify-start w-full',
        // Spacing - vertical padding (py-1 from MUI = 8px = py-2.5 in Tailwind)
        'py-2.5',
        // Dynamic left padding based on nesting level
        paddingLeft,
        // Remove default rounded for list-like appearance
        'rounded-none',
        // Hover state - subtle background (not selected)
        !isSelected && 'hover:bg-accent hover:text-accent-foreground',
        // Selected state - primary color background with contrast text
        isSelected && 'bg-primary text-primary-foreground',
        // Selected hover state - slightly darker primary
        isSelected && 'hover:bg-primary/90',
        // Smooth transitions
        'transition-colors',
        // Custom classes override
        className
      )}
      onClick={onClick}
      aria-label={`Navigate to ${menuItem.name}`}
      aria-current={isSelected ? 'page' : undefined}
      data-state={isSelected ? 'active' : 'inactive'}
      data-testid={`nav-item-${menuItem.id}`}
    >
      {/* Icon wrapper - matches MUI ListItemIcon min-width: 40px */}
      {menuItem.icon && (
        <div className="min-w-[40px] flex items-center justify-start">{menuItem.icon}</div>
      )}

      {/* Label - matches MUI ListItemText with Typography variant="body2" */}
      <span
        className={cn(
          'text-sm',
          // Bold text when selected (matches MUI fontWeight: bold)
          isSelected ? 'font-bold' : 'font-normal'
        )}
      >
        {menuItem.name}
      </span>
    </Button>
  );
};

NavigationItem.displayName = 'NavigationItem';
